import { getConfig } from "@/config";

export interface SignedUrlResponse {
  key: string; // The filename to put in X-Bz-File-Name
  uploadUrl: string; // The B2 Upload URL
  authorizationToken: string; // The B2 Authorization Token
  cdnUrl: string;
  expiresAt: number
}

export interface UploadedAsset {
  assetUrl: string;
  storageKey: string;
}

export const UploadService = {
  /**
   * Request a signed URL for uploading a file
   */
  getSignedUrl: async (filename: string): Promise<SignedUrlResponse> => {
    const config = getConfig();
    const response = await fetch(`${config.VITE_DASHBOARD_API_URL}/api/v1/upload/signed`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", 
      body: JSON.stringify({ filename }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to get signed URL");
    }

    return response.json();
  },

  /**
   * Upload file to the signed URL (Backblaze B2 Native API) with progress tracking
   */
  uploadFileToSignedUrl: async (
    signedUrlData: SignedUrlResponse,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let lastProgress = -1;

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          if (progress !== lastProgress) {
            lastProgress = progress;
            onProgress(progress);
          }
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error("Failed to upload file to storage: " + xhr.statusText));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Failed to upload file to storage: Network error"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Failed to upload file to storage: Upload aborted"));
      });

      xhr.open("POST", signedUrlData.uploadUrl);
      xhr.setRequestHeader("Authorization", signedUrlData.authorizationToken);
      xhr.setRequestHeader("X-Bz-File-Name", signedUrlData.key);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.setRequestHeader("X-Bz-Content-Sha1", "do_not_verify");
      xhr.send(file);
    });
  },

  /**
   * Helper to handle the full flow with progress tracking
   */
  uploadFile: async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    const uploaded = await UploadService.uploadAsset(file, onProgress);
    return uploaded.assetUrl;
  },

  /** Upload and retain both the durable asset URL and storage reference. */
  uploadAsset: async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<UploadedAsset> => {
    const signedUrlData = await UploadService.getSignedUrl(file.name);
    await UploadService.uploadFileToSignedUrl(signedUrlData, file, onProgress);

    return {
      assetUrl: signedUrlData.cdnUrl,
      storageKey: signedUrlData.key,
    };
  }
};
